import { useEffect, useRef, useState, useCallback } from "react";

const transcriptionBasePlayer = typeof window !== "undefined" ? window?.bplayer : null;

let globalActiveAudioId: string | null = null;
let globalListeners: ((activeId: string | null) => void)[] = [];

export function useInsightAudioPlayer(audioBegin?: number, audioEnd?: number, id?: string) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackEnded, setPlaybackEnded] = useState(false);

  const currentPlayerRef = useRef<any>(null);
  const thisContextIdRef = useRef<string>(id || Math.random().toString(36).slice(2));

  useEffect(() => {
    const listener = (activeId: string | null) => {
      if (activeId !== thisContextIdRef.current && isPlaying) {
        setIsPlaying(false);
      }
    };
    globalListeners.push(listener);

    return () => {
      globalListeners = globalListeners.filter((l) => l !== listener);
    };
  }, [isPlaying]);


  const onPlaybackTimeUpdated = useCallback(() => {
    if (
      !transcriptionBasePlayer?.isReady() ||
      audioBegin == null ||
      audioEnd == null ||
      globalActiveAudioId !== thisContextIdRef.current
    )
      return;

    const time = transcriptionBasePlayer.time();
    const outOfBounds = time < audioBegin - 0.01 || time > audioEnd + 0.01;

    if (outOfBounds) {
      transcriptionBasePlayer.pause();
      transcriptionBasePlayer.off("playbackTimeUpdated", onPlaybackTimeUpdated);
      setIsPlaying(false);
      setPlaybackEnded(true);
      globalActiveAudioId = null;
    }
  }, [audioBegin, audioEnd]);

  const playPause = () => {

    if (!audioBegin || !audioEnd || !transcriptionBasePlayer?.isReady()) {
      return;
    }

    if (currentPlayerRef.current && currentPlayerRef.current !== transcriptionBasePlayer) {
      currentPlayerRef.current.pause();
      currentPlayerRef.current.off("playbackTimeUpdated", onPlaybackTimeUpdated);
    }

    if (isPlaying) {
      transcriptionBasePlayer.pause();
      setIsPlaying(false);
      globalActiveAudioId = null;
    } else {
      const currentTime = transcriptionBasePlayer.time();
      const needsSeek =
        playbackEnded || currentTime < audioBegin || currentTime > audioEnd;

      if (needsSeek) {
        transcriptionBasePlayer.seek(audioBegin);
      }

      setTimeout(() => {
        transcriptionBasePlayer.play();

        requestAnimationFrame(() => {
          transcriptionBasePlayer.off("playbackTimeUpdated", onPlaybackTimeUpdated);
          transcriptionBasePlayer.on("playbackTimeUpdated", onPlaybackTimeUpdated);
        });

        currentPlayerRef.current = transcriptionBasePlayer;
        setIsPlaying(true);
        setPlaybackEnded(false);
        globalActiveAudioId = thisContextIdRef.current;
        globalListeners.forEach((fn) => fn(globalActiveAudioId));

      }, 100);
    }
  };

  const stop = () => {
    if (transcriptionBasePlayer?.isReady()) {
      transcriptionBasePlayer.pause();
      setIsPlaying(false);
      globalActiveAudioId = null;
      globalListeners.forEach((fn) => fn(null));
    }
  };

  useEffect(() => {
    const onPause = () => {
      if (globalActiveAudioId === thisContextIdRef.current) {
        setIsPlaying(false);
      }
    };

    transcriptionBasePlayer?.on("playbackPaused", onPause);

    return () => {
      if (currentPlayerRef.current) {
        currentPlayerRef.current.pause();
        currentPlayerRef.current.off("playbackTimeUpdated", onPlaybackTimeUpdated);
        currentPlayerRef.current = null;
      }
      transcriptionBasePlayer?.off("playbackPaused", onPause);
      if (globalActiveAudioId === thisContextIdRef.current) {
        globalActiveAudioId = null;
      }
    };
  }, [onPlaybackTimeUpdated]);

  return {
    isPlaying,
    playbackEnded,
    playPause,
    stop,
  };
}
